import { useCallback, useEffect, useState } from "react";
import { formatDate } from "./core";
import type { StaffPaneProps } from "./console-types";
import { PAYMENTS_CHECKLIST_KEYS, type PaymentsChecklist, type PaymentsChecklistKey } from "../../shared/payments-setup";
import "./payments-setup.css";

// F171 (Tomas Pohl 2026-10-06): "Napojení plateb". Two controls, one data
// source (`GET /api/staff/clients/:id/payments-setup`):
// - the partner's checklist on the step's own workspace, with "hotovo";
// - the admin switch in the client settings that gives a client the step
//   (a manually imported client who got the payment setup outside a purchase).
// Nothing here mails anybody.

interface PaymentsSetupView {
  included: boolean;
  checklist: PaymentsChecklist;
  completedAt: string | null;
  completedByName: string | null;
  stepState: "locked" | "next" | "working" | "done" | null;
}

const KNOWN_ERRORS = [
  "payments_setup_not_included",
  "payments_setup_completed",
  "payments_checklist_incomplete",
  "payments_step_locked"
];

function errorKey(code: string | null | undefined): string {
  return `staffConsole.paymentsSetup.errors.${code && KNOWN_ERRORS.includes(code) ? code : "unavailable"}`;
}

function useSetup(clientId: string) {
  const [setup, setSetup] = useState<PaymentsSetupView | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const load = useCallback(async () => {
    try {
      const response = await fetch(`/api/staff/clients/${encodeURIComponent(clientId)}/payments-setup`, { credentials: "include" });
      if (!response.ok) { setLoadFailed(true); return; }
      const body = await response.json() as { setup?: PaymentsSetupView };
      setSetup(body.setup ?? null);
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    }
  }, [clientId]);
  useEffect(() => { void load(); }, [load]);
  return { setup, setSetup, loadFailed, load };
}

async function readSetup(response: Response): Promise<{ setup: PaymentsSetupView | null; error: string | null }> {
  const body = await response.json().catch(() => null) as { setup?: PaymentsSetupView; error?: string } | null;
  return response.ok && body?.setup ? { setup: body.setup, error: null } : { setup: null, error: body?.error ?? "unavailable" };
}

/** The step workspace: the partner's checklist and the "done" button. */
export function PaymentsSetupCard(props: StaffPaneProps) {
  const clientId = props.detail.client.id;
  const t = props.t;
  const { setup, setSetup, loadFailed } = useSetup(clientId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const base = `/api/staff/clients/${encodeURIComponent(clientId)}/payments-setup`;

  async function mutate(path: string, method: "PUT" | "POST", body?: unknown) {
    setBusy(true);
    setError(null);
    try {
      const result = await readSetup(await props.run(path, { method, ...(body ? { body } : {}) }, { refresh: true }));
      if (result.setup) setSetup(result.setup);
      else setError(result.error);
    } finally {
      setBusy(false);
    }
  }

  if (loadFailed && !setup) {
    return <article className="staff-card"><p className="message error-message">{t(errorKey("unavailable"))}</p></article>;
  }
  if (!setup) return <article className="staff-card"><p className="staff-empty">{t("staffConsole.paymentsSetup.loading")}</p></article>;
  if (!setup.included) {
    return (
      <article className="staff-card" data-payments-setup="not-included">
        <h3>{t("staffConsole.paymentsSetup.title")}</h3>
        <p className="staff-empty">{t("staffConsole.paymentsSetup.notIncluded")}</p>
      </article>
    );
  }
  const done = Boolean(setup.completedAt);
  const allTicked = PAYMENTS_CHECKLIST_KEYS.every((key) => setup.checklist[key] === true);
  const locked = setup.stepState === "locked";
  return (
    <article className="staff-card payments-setup-card" data-payments-setup={done ? "done" : "open"}>
      <h3>{t("staffConsole.paymentsSetup.title")}</h3>
      <p className="staff-empty">{t("staffConsole.paymentsSetup.hint")}</p>
      {locked && <p className="staff-empty">{t("staffConsole.paymentsSetup.lockedNote")}</p>}
      <ul className="payments-checklist">
        {PAYMENTS_CHECKLIST_KEYS.map((key: PaymentsChecklistKey) => (
          <li key={key}>
            <label>
              <input
                type="checkbox"
                checked={setup.checklist[key] === true}
                disabled={busy || done}
                onChange={(event) => void mutate(`${base}/checklist`, "PUT", { items: { [key]: event.target.checked } })}
              />
              <span>
                <strong>{t(`staffConsole.paymentsSetup.items.${key}.title`)}</strong>
                <small>{t(`staffConsole.paymentsSetup.items.${key}.hint`)}</small>
              </span>
            </label>
          </li>
        ))}
      </ul>
      {done ? (
        <>
          <p data-payments-setup-done="true">
            {t("staffConsole.paymentsSetup.doneLine")
              .replace("{name}", setup.completedByName ?? "")
              .replace("{date}", formatDate(setup.completedAt as string, props.locale))}
          </p>
          <div className="button-row">
            <button type="button" className="secondary-button compact-button" disabled={busy} onClick={() => void mutate(`${base}/reopen`, "POST")}>
              {t("staffConsole.paymentsSetup.reopen")}
            </button>
          </div>
        </>
      ) : (
        <div className="button-row">
          <button
            type="button"
            className="primary-button compact-button"
            disabled={busy || !allTicked || locked}
            onClick={() => void mutate(`${base}/complete`, "POST")}
          >
            {t("staffConsole.paymentsSetup.complete")}
          </button>
          {!allTicked && <small className="staff-empty">{t("staffConsole.paymentsSetup.completeNeedsAll")}</small>}
        </div>
      )}
      {error && <p className="message error-message" role="alert">{t(errorKey(error))}</p>}
    </article>
  );
}

/** Client settings, admin only: give or take the step. */
export function PaymentsSetupFlagCard(props: StaffPaneProps) {
  const clientId = props.detail.client.id;
  const t = props.t;
  const { setup, setSetup, loadFailed } = useSetup(clientId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function toggle(enabled: boolean) {
    setBusy(true);
    setError(null);
    try {
      const result = await readSetup(await props.run(
        `/api/admin/clients/${encodeURIComponent(clientId)}/payments-setup`,
        { method: "PUT", body: { enabled } },
        { refresh: true }
      ));
      if (result.setup) setSetup(result.setup);
      else setError(result.error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="staff-card" data-payments-setup-flag={setup ? String(setup.included) : "loading"}>
      <h3>{t("staffConsole.paymentsSetup.flagTitle")}</h3>
      <p className="staff-empty">{t("staffConsole.paymentsSetup.flagHint")}</p>
      {loadFailed && !setup && <p className="message error-message">{t(errorKey("unavailable"))}</p>}
      {setup && (
        <label className="payments-flag">
          <input type="checkbox" checked={setup.included} disabled={busy} onChange={(event) => void toggle(event.target.checked)} />
          <span>{t(setup.included ? "staffConsole.paymentsSetup.flagOn" : "staffConsole.paymentsSetup.flagOff")}</span>
        </label>
      )}
      {error && <p className="message error-message" role="alert">{t(errorKey(error))}</p>}
    </article>
  );
}
